import { Api } from 'types/api-types';

type ApisObject = {
  [key: string]: Api;
};

// A query string of the values that are set.
const toQuery = (values: Record<string, string | undefined>) =>
  Object.entries(values)
    .filter(([, value]) => value !== undefined && value !== '')
    .map(([key, value]) => `${key}=${encodeURIComponent(value as string)}`)
    .join('&');

export const apis: ApisObject = {
  deletedDiamonds: {
    url: () => `DeletedDiamond/filter`,
    method: 'post'
  },
  restoreLots: {
    url: () => `DeletedDiamond/RestoreLots`,
    method: 'post'
  },
  // The days from the from date to the to date (YYYY-MM-DD), both included, newest first.
  diamondSummary: {
    url: ({ from, to }: { from: string; to: string }) =>
      `DiamondSummary?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
    method: 'get'
  },
  // Accounts > Payment History: the legacy (pre-Salesforce) payments of an account. A payment is named by its kind
  // (subscription or general) and number, as the two kinds are numbered apart.
  accountPayments: {
    url: (accountId: number) => `AccountPayments/${accountId}`,
    method: 'get'
  },
  accountPayment: {
    url: ({ accountId, kind, paymentId }: { accountId: number; kind: string; paymentId: number }) =>
      `AccountPayments/${accountId}/${kind}/${paymentId}`,
    method: 'get'
  },
  // The receipt PDF as base64 in the usual envelope.
  accountPaymentReceipt: {
    url: ({ accountId, kind, paymentId }: { accountId: number; kind: string; paymentId: number }) =>
      `AccountPayments/${accountId}/${kind}/${paymentId}/Receipt`,
    method: 'get'
  },
  // Members > Member Ratings. The filters go in the query string; empty ones are left out.
  memberRatingLookups: {
    url: () => `MemberRatings/Lookups`,
    method: 'get'
  },
  memberRatings: {
    url: ({
      filter,
      page,
      pageSize
    }: {
      filter: Record<string, string | undefined>;
      page: number;
      pageSize: number;
    }) => `MemberRatings?${toQuery({ ...filter, page: `${page}`, pageSize: `${pageSize}` })}`,
    method: 'get'
  },
  memberRatingsExport: {
    url: (filter: Record<string, string | undefined>) => `MemberRatings/Export?${toQuery(filter)}`,
    method: 'get'
  },
  memberRatingUpdate: {
    url: ({ fromAccountId, aboutAccountId }: { fromAccountId: number; aboutAccountId: number }) =>
      `MemberRatings/${fromAccountId}/${aboutAccountId}`,
    method: 'put'
  },
  memberRatingDelete: {
    url: ({ fromAccountId, aboutAccountId }: { fromAccountId: number; aboutAccountId: number }) =>
      `MemberRatings/${fromAccountId}/${aboutAccountId}`,
    method: 'delete'
  },
  activeUploads: {
    url: () => `Upload/Monitor/ActiveUploads`,
    method: 'get'
  },
  uploadsHistory: {
    url: () => `Upload/UploadsHistory`,
    method: 'post'
  },
  exportUploadToExcel: {
    url: ({ accountId, uploadId }) => `Upload/ExportToExcel/${accountId}/${uploadId}`,
    method: 'post'
  },
  priceUploadNextPublicationDate: {
    url: () => `PriceUpload/NextPublicationDate`,
    method: 'get'
  },
  // Starts the upload, which runs in the background longer than the API gateway waits; its status
  // is then followed with priceUploadExcelStatus.
  priceUploadExcel: {
    url: () => `PriceUpload/Excel`,
    method: 'post'
  },
  // The status of an upload, or of the latest one without an id.
  priceUploadExcelStatus: {
    url: (uploadId?: string) =>
      uploadId
        ? `PriceUpload/Excel/Status/${encodeURIComponent(uploadId)}`
        : `PriceUpload/Excel/Status`,
    method: 'get'
  },
  // The monthly parcels Excel: its first sheet holds the parcel prices of the date (the 1st of their month).
  priceUploadParcels: {
    url: () => `PriceUpload/Parcels`,
    method: 'post'
  },
  clearCache: {
    url: ({ accountId }) => `Upload/AddInvalidAccount/${accountId}`,
    method: 'post'
  },
  retryUpload: {
    url: ({ uploadId }) => `Upload/RetryUpload/${uploadId}`,
    method: 'post'
  },
  redoServiceUpload: {
    url: ({ uploadId }) => `Upload/RedoUpload/${uploadId}`,
    method: 'post'
  },
  removeServiceUpload: {
    url: ({ uploadId }) => `Upload/Remove/${uploadId}`,
    method: 'post'
  },
  accountAutoComplete: {
    url: ({ onlyMembers, term }) => `Device/AccountAutoComplete/${onlyMembers}/${term}`,
    method: 'get'
  },
  getDevices: {
    url: () => `Device/GetDevices`,
    method: 'post'
  },
  deviceStatus: {
    url: () => `Device/DeviceStatus`,
    method: 'put'
  },
  exportDevicesToExcel: {
    url: () => `Device/GetDevices/ExportToExcel`,
    method: 'post'
  },
  getAccountInformation: {
    url: () => `Device/GetAccountInformation`,
    method: 'post'
  },
  getDefaultSettings: {
    url: ({ country, city, packageName, isPrimaryMember }) =>
      `Device/DefaultSettings?country=${country}&city=${city}&packageName=${packageName}&isPrimaryMember=${isPrimaryMember}`,
    method: 'get'
  },
  getAllDefaultSettings: {
    url: () => `Device/DefaultSettings/All`,
    method: 'get'
  },
  updateDefaultSettings: {
    url: () => `Device/DefaultSettings`,
    method: 'post'
  },
  deleteDefaultSetting: {
    url: ({ DefaultSettingId }) => `Device/DefaultSettings/${DefaultSettingId}`,
    method: 'delete'
  },
  deleteMobileDevices: {
    url: () => `Device/DeleteMobileDevices`,
    method: 'delete'
  },
  getUserSettings: {
    url: ({ UserId }) => `Device/UserSettings/${UserId}`,
    method: 'get'
  },
  updateUserSettings: {
    url: () => `Device/UserSettings`,
    method: 'put'
  },
  userAdditionalSettings: {
    url: () => `Device/UserAdditionalSettings`,
    method: 'post'
  },
  CertRefresh: {
    url: () => `CertRefresh/Certificate`,
    method: 'post'
  },
  companyLogo: {
    url: (accountId: string | number) => `BackOffice/Account/${accountId}/Logo`,
    method: 'get'
  },
  currentContactInfo: {
    url: () => `BackOffice/CurrentContactInfo`,
    method: 'get'
  },
  uploadColumnsGetDictionary: {
    url: () => `UploadColumns/Dictionary`,
    method: 'get',
    gateway: 'gemstones'
  },
  uploadColumnsGetList: {
    url: ({ pageNum, perPage, field }: { pageNum: number; perPage: number; field: string }) =>
      `UploadColumns?CurrentPage=${pageNum}&RecordsPerPage=${perPage}&field=${field}`,
    method: 'get',
    gateway: 'gemstones'
  },
  uploadColumnsAddColumn: {
    url: () => `UploadColumns`,
    method: 'post',
    gateway: 'gemstones'
  },
  uploadColumnsExport: {
    url: ({ pageNum, perPage, field }: { pageNum: number; perPage: number; field: string }) =>
      `UploadColumns/Export?CurrentPage=${pageNum}&RecordsPerPage=${perPage}&field=${field}`,
    method: 'get',
    gateway: 'gemstones'
  },
  uploadColumnsDeleteById: {
    url: (id: number) => `UploadColumns/${id}`,
    method: 'delete',
    gateway: 'gemstones'
  },
  uploadColumnsUpdateById: {
    url: (id: number) => `UploadColumns/${id}`,
    method: 'put',
    gateway: 'gemstones'
  },
  validValuesGetDictionary: {
    url: () => `ValidValues/Dictionary`,
    method: 'get',
    gateway: 'gemstones'
  },
  validValuesGetList: {
    url: ({
      pageNum,
      perPage,
      field,
      value
    }: {
      pageNum: number;
      perPage: number;
      field: string;
      value: string;
    }) =>
      `ValidValues?CurrentPage=${pageNum}&RecordsPerPage=${perPage}&field=${field}&value=${value}`,
    method: 'get',
    gateway: 'gemstones'
  },
  validValuesAddColumn: {
    url: () => `ValidValues`,
    method: 'post',
    gateway: 'gemstones'
  },
  validValuesExport: {
    url: ({ pageNum, perPage, field }: { pageNum: number; perPage: number; field: string }) =>
      `ValidValues/Export?CurrentPage=${pageNum}&RecordsPerPage=${perPage}&field=${field}`,
    method: 'get',
    gateway: 'gemstones'
  },
  validValuesDeleteById: {
    url: (id: number) => `ValidValues/${id}`,
    method: 'delete',
    gateway: 'gemstones'
  },
  validValuesUpdateById: {
    url: (id: number) => `ValidValues/${id}`,
    method: 'put',
    gateway: 'gemstones'
  },
  valuesManagmentGetList: {
    url: ({ pageNum, perPage, field }: { pageNum: number; perPage: number; field: string }) =>
      `ValuesManagement?CurrentPage=${pageNum}&RecordsPerPage=${perPage}&field=${field}`,
    method: 'get',
    gateway: 'gemstones'
  },
  valuesManagmentExport: {
    url: ({ pageNum, perPage, field }: { pageNum: number; perPage: number; field: string }) =>
      `ValuesManagement/Export?CurrentPage=${pageNum}&RecordsPerPage=${perPage}&field=${field}`,
    method: 'get',
    gateway: 'gemstones'
  },
  valuesManagmentAddValue: {
    url: () => `ValuesManagement`,
    method: 'post',
    gateway: 'gemstones'
  },
  valuesManagmentUpdateVal: {
    url: () => `ValuesManagement`,
    method: 'put',
    gateway: 'gemstones'
  },
  valuesManagmentDeleteById: {
    url: (id: number) => `ValuesManagement/${id}`,
    method: 'delete',
    gateway: 'gemstones'
  },
  getSourceVerifications: {
    url: () => `SourceVerification/list`,
    method: 'post'
  },
  updateVerificationStatus: {
    url: () => `SourceVerification/UpdateVerificationStatus`,
    method: 'post'
  },
  updateSourceVerification: {
    url: () => `SourceVerification/UpdateDiamond`,
    method: 'put'
  },
  approveDocument: {
    url: (diamondID: number) => `SourceVerification/ApproveDocument/Diamond/${diamondID}`,
    method: 'post'
  },
  rejectDocument: {
    url: (diamondID: number) => `SourceVerification/RejectDocument/Diamond/${diamondID}`,
    method: 'post'
  },
  getSourceVerificationFilterOptions: {
    url: () => `SourceVerification/FilterValues`,
    method: 'get'
  },
  uploadDocument: {
    url: ({ sellerID, diamondID }: { sellerID: number; diamondID: number }) =>
      `SourceVerification/Upload/Account/${sellerID}/Diamond/${diamondID}`,
    method: 'post'
  },
  connectDocument: {
    url: ({ sellerID, diamondID }: { sellerID: number; diamondID: number }) =>
      `SourceVerification/Connect/Account/${sellerID}/Diamond/${diamondID}`,
    method: 'post'
  },
  checkUploadStatus: {
    url: ({ sellerID }: { sellerID: number }) =>
      `SourceVerification/UploadStatus?SellerID=${sellerID}`,
    method: 'post'
  },
  searchLogsSummary: {
    url: () => `searchlogs/summary`,
    method: 'post'
  },
  searchLimitPeriodSettings: {
    url: () => `searchlogs/searchLimitPeriodSettings`,
    method: 'get'
  },
  upsertSearchLimitPeriodSetting: {
    url: () => `searchlogs/searchLimitPeriodSetting/upsert`,
    method: 'post'
  },
  searchLimitAccountContacts: {
    url: () => `searchlogs/searchLimitAccountContacts`,
    method: 'post'
  },
  upsertSearchLimitAccountContact: {
    url: () => `searchlogs/searchLimitAccountContact/upsert`,
    method: 'post'
  },
  searchLimitConfigs: {
    url: () => `searchlogs/searchLimitConfigs`,
    method: 'post'
  },
  upsertSearchLimitConfig: {
    url: () => `searchlogs/searchLimitConfig/upsert`,
    method: 'post'
  },
  deleteSearchLimitConfig: {
    url: () => `searchlogs/searchLimitConfig/delete`,
    method: 'post'
  },
  accountSearchTiers: {
    url: () => `searchlogs/accountSearchTiers`,
    method: 'post'
  },
  upsertAccountSearchTier: {
    url: () => `searchlogs/accountSearchTier/upsert`,
    method: 'post'
  },
  searchLimitEvents: {
    url: () => `searchlogs/searchLimitEvents`,
    method: 'post'
  },
  complianceDocumentTypes: {
    url: () => `ComplianceConfiguration/DocumentTypes`,
    method: 'get'
  },
  complianceDocumentTypeCreate: {
    url: () => `ComplianceConfiguration/DocumentTypes`,
    method: 'post'
  },
  complianceDocumentTypeUpdate: {
    url: () => `ComplianceConfiguration/DocumentTypes`,
    method: 'put'
  },
  complianceDocumentTypeDelete: {
    url: (verificationItemId: number) =>
      `ComplianceConfiguration/DocumentTypes/${verificationItemId}`,
    method: 'delete'
  },
  complianceOffices: {
    url: () => `ComplianceConfiguration/Offices`,
    method: 'get'
  },
  complianceCountries: {
    url: () => `ComplianceConfiguration/Countries`,
    method: 'get'
  },
  complianceDocumentCategories: {
    url: () => `ComplianceConfiguration/DocumentCategories`,
    method: 'get'
  },
  complianceEntityTypes: {
    url: () => `ComplianceConfiguration/EntityTypes`,
    method: 'get'
  },
  complianceRequirementLevels: {
    url: () => `ComplianceConfiguration/RequirementLevels`,
    method: 'get'
  },
  complianceRequirements: {
    url: ({
      officeCode,
      countryIso3,
      entityTypeCode
    }: {
      officeCode?: string;
      countryIso3?: string;
      entityTypeCode?: string;
    }) => {
      const query = [
        officeCode ? `officeCode=${encodeURIComponent(officeCode)}` : '',
        countryIso3 ? `countryIso3=${encodeURIComponent(countryIso3)}` : '',
        entityTypeCode ? `entityTypeCode=${encodeURIComponent(entityTypeCode)}` : ''
      ]
        .filter(part => part !== '')
        .join('&');
      return `ComplianceConfiguration/Requirements${query ? `?${query}` : ''}`;
    },
    method: 'get'
  },
  complianceRequirementsUpsert: {
    url: () => `ComplianceConfiguration/Requirements`,
    method: 'put'
  },
  complianceRequirementDelete: {
    url: (id: number) => `ComplianceConfiguration/Requirements/${id}`,
    method: 'delete'
  },
  complianceChecklist: {
    url: ({
      officeId,
      countryIso3,
      entityTypeId
    }: {
      officeId: number;
      countryIso3: string;
      entityTypeId?: number | null;
    }) =>
      `ComplianceConfiguration/Checklist?officeId=${officeId}&countryIso3=${encodeURIComponent(
        countryIso3
      )}${entityTypeId ? `&entityTypeId=${entityTypeId}` : ''}`,
    method: 'get'
  },
  complianceTemplateUpload: {
    url: () => `ComplianceConfiguration/Requirements/Template`,
    method: 'post'
  },
  // Build a Ring orders — the operations grid. The back-office API proxies the
  // ring builder service's internal admin endpoints; `stage` repeats per value
  // (stage=a&stage=b), which is how the proxy binds its string[].
  ringOrders: {
    url: ({
      stages,
      search,
      page,
      pageSize
    }: {
      stages?: string[];
      search?: string;
      page?: number;
      pageSize?: number;
    } = {}) => {
      const query = [
        ...(stages ?? []).map(stage => `stage=${encodeURIComponent(stage)}`),
        search ? `search=${encodeURIComponent(search)}` : '',
        `page=${page ?? 1}`,
        `pageSize=${pageSize ?? 50}`
      ]
        .filter(part => part !== '')
        .join('&');
      return `RingOrders?${query}`;
    },
    method: 'get'
  },
  // The body is { stage, etag } — the etag is the row's If-Match value, carried
  // in the body because the AWS gateway's CORS allow-list blocks the If-Match
  // header from browsers; the back-office API forwards it as the real header.
  ringOrderUpdateStage: {
    url: (id: string) => `RingOrders/${encodeURIComponent(id)}/stage`,
    method: 'put'
  },
  // The weekly price list. The back-office API proxies the price list service (Share_Pricelist).
  priceListDates: {
    url: () => `PriceList/Dates`,
    method: 'get'
  },
  priceListSummary: {
    url: ({ shapeId, currencyId, date }: { shapeId: number; currencyId: number; date: string }) =>
      `PriceList/Summary?shapeId=${shapeId}&currencyId=${currencyId}&date=${encodeURIComponent(
        date
      )}`,
    method: 'get'
  },
  priceListParcelDates: {
    url: () => `PriceList/Parcels/Dates`,
    method: 'get'
  },
  // The parcels price list a date carries: the latest one dated on or before it.
  priceListParcelsSummary: {
    url: (date: string) => `PriceList/Parcels/Summary?date=${encodeURIComponent(date)}`,
    method: 'get'
  },
  // The document's PDF as base64 in the usual envelope: the API gateway would corrupt a binary body.
  priceListPdf: {
    url: ({ document, date }: { document: string; date: string }) =>
      `PriceList/Pdf/${encodeURIComponent(document)}?date=${encodeURIComponent(date)}`,
    method: 'get'
  },
  priceListTexts: {
    url: (date: string) => `PriceList/Texts?date=${encodeURIComponent(date)}`,
    method: 'get'
  },
  priceListSaveTexts: {
    url: (date: string) => `PriceList/Texts?date=${encodeURIComponent(date)}`,
    method: 'put'
  },
  // Whether the news of texts that are not saved yet fits the boxes that show it, as it prints.
  priceListCheckTextsFit: {
    url: (date: string) => `PriceList/Texts/Fit?date=${encodeURIComponent(date)}`,
    method: 'post'
  },
  // Whether the saved news of a document fits its boxes.
  priceListTextFit: {
    url: ({ document, date }: { document: string; date: string }) =>
      `PriceList/TextFit/${encodeURIComponent(document)}?date=${encodeURIComponent(date)}`,
    method: 'get'
  },
  priceListCurrencyRates: {
    url: () => `PriceList/CurrencyRates`,
    method: 'get'
  },
  priceListAddCurrencyRate: {
    url: () => `PriceList/CurrencyRates`,
    method: 'post'
  },
  priceListDateRates: {
    url: () => `PriceList/PriceListCurrencyRates`,
    method: 'get'
  },
  priceListLatestRates: {
    url: () => `PriceList/PriceListCurrencyRates/Rates`,
    method: 'get'
  },
  // Attaching a rate rebuilds that date's rupee prices from its dollar prices.
  priceListAttachRate: {
    url: (date: string) => `PriceList/PriceListCurrencyRates?date=${encodeURIComponent(date)}`,
    method: 'put'
  },
  // The rupee rate of the next price list: taken automatically on Thursday, or entered by hand.
  priceListRupeeRate: {
    url: () => `PriceList/RupeeRate`,
    method: 'get'
  },
  priceListSetRupeeRate: {
    url: () => `PriceList/RupeeRate`,
    method: 'put'
  },
  priceListTemplate: {
    url: (template: string) => `PriceList/Template/${encodeURIComponent(template)}`,
    method: 'get'
  },
  priceListSaveTemplate: {
    url: (template: string) => `PriceList/Template/${encodeURIComponent(template)}`,
    method: 'put'
  },
  // The template becomes the default; the replaced default and the edited copy are kept in the archive.
  priceListSaveDefaultTemplate: {
    url: (template: string) => `PriceList/Template/${encodeURIComponent(template)}/Default`,
    method: 'put'
  },
  // Removes the edited template (it is kept in the archive), so the default one is used again.
  priceListResetTemplate: {
    url: (template: string) => `PriceList/Template/${encodeURIComponent(template)}/Current`,
    method: 'delete'
  },
  // The archived copies of a template, latest first.
  priceListTemplateVersions: {
    url: (template: string) => `PriceList/Template/${encodeURIComponent(template)}/Versions`,
    method: 'get'
  },
  priceListTemplateVersion: {
    url: ({ template, versionId }: { template: string; versionId: string }) =>
      `PriceList/Template/${encodeURIComponent(template)}/Versions/${encodeURIComponent(
        versionId
      )}`,
    method: 'get'
  },
  priceListPublishStatus: {
    url: () => `PriceList/Publish`,
    method: 'get'
  },
  // Starts publishing in the background: the week's PDFs and the Excel upload's files go to the live keys
  // the site reads. The run is followed through priceListPublishStatus (lastRun).
  priceListPublish: {
    url: () => `PriceList/Publish`,
    method: 'post'
  },
  // The RAPI report (was OFC Prices/PriceResearch/RAPI.aspx) of a date range.
  priceListRapi: {
    url: ({ type, from, to }: { type: string; from: string; to: string }) =>
      `PriceList/Rapi?type=${type}&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
    method: 'get'
  },
  // The price reports (was the legacy OFC page Price Report Center spec A3): a report is started, built in
  // the background and followed through its status, which gives the file's download link once built.
  priceReportStart: {
    url: () => `PriceReports`,
    method: 'post'
  },
  priceReportStatus: {
    url: (reportId: string) => `PriceReports/Status/${encodeURIComponent(reportId)}`,
    method: 'get'
  },
  priceReportShapes: {
    url: () => `PriceReports/Shapes`,
    method: 'get'
  },
  priceReportPriceLogDates: {
    url: (shapeId: string) => `PriceReports/PriceLogDates?shapeId=${encodeURIComponent(shapeId)}`,
    method: 'get'
  },
  priceReportSnapshotDates: {
    url: () => `PriceReports/SnapshotDates`,
    method: 'get'
  },
  // The RapNet price lists and reports members download (was OFC Rapnet/PriceList/List.aspx), newest first.
  // Every filter is optional; the dates (YYYY-MM-DD) are both included.
  rapNetPriceLists: {
    url: ({
      listId,
      typeId,
      from,
      to
    }: {
      listId?: number;
      typeId?: number;
      from?: string;
      to?: string;
    }) => {
      const query = [
        listId ? `listId=${listId}` : '',
        typeId ? `typeId=${typeId}` : '',
        from ? `from=${encodeURIComponent(from)}` : '',
        to ? `to=${encodeURIComponent(to)}` : ''
      ]
        .filter(part => part !== '')
        .join('&');
      return `PriceList/RapNetPriceLists${query ? `?${query}` : ''}`;
    },
    method: 'get'
  },
  rapNetPriceListTypes: {
    url: () => `PriceList/RapNetPriceLists/Types`,
    method: 'get'
  },
  // The list's PDF as base64 in the usual envelope, like priceListPdf.
  rapNetPriceListPdf: {
    url: (listId?: number) => `PriceList/RapNetPriceLists/${listId}/Pdf`,
    method: 'get'
  },
  rapNetPriceListDelete: {
    url: (listId?: number) => `PriceList/RapNetPriceLists/${listId}`,
    method: 'delete'
  },
  // The draft's pages as images, rendered as the PDF is: filled with a week's data, or without data
  // when no date is given.
  priceListPreviewTemplate: {
    url: ({ template, date }: { template: string; date: string }) =>
      `PriceList/Template/${encodeURIComponent(template)}/Preview${
        date ? `?date=${encodeURIComponent(date)}` : ''
      }`,
    method: 'post'
  }
};
